import { create } from 'zustand';
import { adminResolvedEmailsApi } from '@api/api-config.ts';
import { AdminResolvedEmailListResponse, AdminResolvedEmailDetailResponse, AdminResolveEmailRequest } from '@api/generated/portal-be-admin';

export interface ResolvedEmailFilters {
  search?: string;
  status?: string;
  /** Skutecne doruceni z Mailgun Events API — jiny udaj nez `status` (ten je jen "predano Mailgunu"). */
  deliveryStatus?: string;
  /** Zkratka na vse, co nedoslo (hard bounce, odmitnuto, neodeslano) — resi se na backendu. */
  undeliveredOnly?: boolean;
  organizationId?: number;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface AdminResolvedEmailStoreState {
  loading: boolean;
  error: string | null;
  data: AdminResolvedEmailListResponse[] | null;
  pagination: PaginationInfo | null;
  detail: AdminResolvedEmailDetailResponse | null;
  detailLoading: boolean;
  retrying: boolean;
  retryError: string | null;
  lastFilters: ResolvedEmailFilters | null;
  loadResolvedEmails: (filters: ResolvedEmailFilters) => Promise<void>;
  loadDetail: (id: number) => Promise<void>;
  retryEmail: (id: number) => Promise<void>;
  /** Vraci id nove instance mailu, kdyz se posilalo znovu — jinak null. */
  resolveEmail: (id: number, request: AdminResolveEmailRequest) => Promise<number | null>;
  clearDetail: () => void;
}

/** Hlaska z advice v portal-be-admin; bez ni by uzivatel videl jen "Request failed with status 500". */
const extractApiError = (error: unknown): string | null => {
  const data = (error as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
  return data?.message || data?.detail || (error instanceof Error ? error.message : null);
};

export const useAdminResolvedEmailStore = create<AdminResolvedEmailStoreState>((set, get) => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  detail: null,
  detailLoading: false,
  retrying: false,
  retryError: null,
  lastFilters: null,

  loadResolvedEmails: async (filters: ResolvedEmailFilters) => {
    try {
      set({ loading: true, error: null, lastFilters: filters });
      const response = await adminResolvedEmailsApi.getAdminResolvedEmails({
        page: filters.page,
        size: filters.size,
        search: filters.search,
        status: filters.status as any,
        deliveryStatus: filters.deliveryStatus as any,
        undeliveredOnly: filters.undeliveredOnly,
        organizationId: filters.organizationId,
      });
      const result = response.data;
      set({
        loading: false,
        error: null,
        data: result.items || [],
        pagination: {
          page: result.page,
          size: result.size,
          total: result.total,
          totalPages: result.totalPages,
          hasPrev: result.hasPrev,
          hasNext: result.hasNext,
        },
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba pri nacitani emailu',
      });
    }
  },

  loadDetail: async (id: number) => {
    try {
      set({ detailLoading: true, detail: null });
      const response = await adminResolvedEmailsApi.getAdminResolvedEmailDetail({ id });
      set({ detailLoading: false, detail: response.data });
    } catch (error) {
      set({ detailLoading: false, detail: null });
    }
  },

  retryEmail: async (id: number) => {
    try {
      set({ retrying: true, retryError: null });
      const response = await adminResolvedEmailsApi.retryAdminResolvedEmail({ id });
      set({ retrying: false, detail: response.data });
      // refetch seznamu, aby se zmena stavu promitla do tabulky
      const filters = get().lastFilters;
      if (filters) {
        await get().loadResolvedEmails(filters);
      }
    } catch (error) {
      set({
        retrying: false,
        retryError: error instanceof Error ? error.message : 'Chyba pri znovuodeslani emailu',
      });
    }
  },

  resolveEmail: async (id: number, request: AdminResolveEmailRequest) => {
    set({ retrying: true, retryError: null });
    try {
      const response = await adminResolvedEmailsApi.resolveAdminResolvedEmail({ id, adminResolveEmailRequest: request });
      set({ retrying: false, detail: response.data.resolved });
      // Vyresenim mail mizi z filtru "jen nedorucene", takze seznam musi znovu.
      const filters = get().lastFilters;
      if (filters) {
        await get().loadResolvedEmails(filters);
      }
      return response.data.resentAsId ?? null;
    } catch (error) {
      set({
        retrying: false,
        // Backend vraci konkretni duvod (napr. ze u neexistujici schranky je potreba jina adresa) —
        // obecna hlaska by uzivatele nechala hadat, proc to nejde.
        retryError: extractApiError(error) ?? 'Chyba pri reseni emailu',
      });
      return null;
    }
  },

  clearDetail: () => set({ detail: null, retryError: null }),
}));
